import styled from '@emotion/styled';
import { getMFEConfig } from '@sunrun/sales-experience-shared';
import { useMemo, useRef } from 'react';
import { publishMessage, useSubscribe } from 'helpers/messaging';
import type { Message } from 'helpers/messaging';
import { useHybridToken } from 'providers/Auth';

type CoachingIFrameProps = {
  sessionId?: string;
};

const CoachingIFrame = ({ sessionId }: CoachingIFrameProps) => {
  const ref = useRef<HTMLIFrameElement | null>(null);
  const { hybridToken, hybridTokenType } = useHybridToken();
  // The id seeds the initial load only. The nested Next.js app owns session
  // selection after mount, so recomputing src when the rep picks a different
  // session would reload it mid-interaction.
  const initialSessionIdRef = useRef(sessionId);
  const { src, title } = useMemo(() => {
    const config = getMFEConfig('coaching', 'coaching');
    const baseUrl = config.iFrameUrl.replace(/\/$/, '');
    return {
      src: initialSessionIdRef.current
        ? `${baseUrl}/${encodeURIComponent(initialSessionIdRef.current)}`
        : baseUrl,
      title: config.title,
    };
  }, []);

  useSubscribe((message: Message) => {
    if (message.source === 'Chat' && message.type === 'QUERY:AUTH_TOKEN') {
      if (hybridToken && ref.current) {
        ref.current.contentWindow?.postMessage(
          {
            type: 'EVENT:AUTH_TOKEN',
            source: 'Dashboard',
            payload: { token: hybridToken, tokenType: hybridTokenType },
          },
          { targetOrigin: '*' },
        );
      }
      return;
    }

    if (message.source === 'Chat') {
      publishMessage({ ...message, source: 'Dashboard' });
    }

    if (message.source === 'Auth') {
      if (ref.current) {
        ref.current.contentWindow?.postMessage(
          { ...message, source: 'Dashboard' },
          { targetOrigin: '*' },
        );
      }
    }
  });

  return (
    <IFrameContainer
      ref={ref}
      src={src}
      title={title}
      allow="clipboard-write;"
    />
  );
};

const IFrameContainer = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  flex: 1;
  display: block;
`;

export { CoachingIFrame };
